import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const jumpingInternationalMonacoEn: ChauffeurPage = {
  slug: "jumping-international-monaco",
  title: "Private chauffeur Jumping International Monaco – VIP VTC",
  subtitle:
    "Private chauffeur during the Jumping International de Monaco: Port Hercule access, Nice airport meet-and-greet and daily hire around the show-jumping event.",
  keywords: [
    "jumping international monaco chauffeur",
    "monaco show jumping chauffeur",
    "global champions tour chauffeur monaco",
  ],
  hero: {
    title: "Private chauffeur during the Jumping International de Monaco",
    subtitle:
      "Longines Global Champions Tour on the quays of Port Hercule: a dedicated chauffeur when Monaco closes and parking disappears.",
    keywords: [
      "Jumping Monaco chauffeur",
      "Port Hercule VTC",
      "Global Champions Tour chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the Jumping International de Monaco",
    badges: ["VIP airport welcome", "Port Hercule drop-off", "Hourly hire"],
  },
  benefits: {
    heading: "Why a chauffeur during the Jumping",
    items: [
      {
        title: "Port Hercule without parking",
        description:
          "The quays host the temporary arena: reduced roads and saturated car parks. The chauffeur drops you as close as possible and leaves, with no circling.",
      },
      {
        title: "Nice airport welcome",
        description:
          "Nice Côte d'Azur airport is about thirty minutes away. Name-board welcome, flight tracking and a direct transfer to your Monaco hotel.",
      },
      {
        title: "Event schedule handled",
        description:
          "Daytime and evening classes in late June: the chauffeur times pick-ups around the show-jumping programme.",
      },
      {
        title: "Hospitality and evenings",
        description:
          "Paddock, dinners and Carré d'Or hotels: late returns with a chauffeur who waits.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during the Jumping de Monaco",
    services: [
      {
        title: "Nice airport meet-and-greet",
        text: "Nice ↔ Monaco transfer with flight tracking across the Jumping weekend.",
      },
      {
        title: "Port Hercule drop-off",
        text: "Approach as close as possible to the quays within the closures around the show-jumping arena.",
      },
      {
        title: "Full-day hire",
        text: "A dedicated chauffeur by the hour or day to chain classes, hospitality and meetings.",
      },
      {
        title: "Evenings & dinners",
        text: "Carré d'Or restaurants, Monte-Carlo Bay and Monaco palaces, with a guaranteed late-night return.",
      },
      {
        title: "Guest & partner shuttles",
        text: "Multi-vehicle coordination for delegations, sponsors and VIP guests.",
      },
    ],
  },
  locations: {
    heading: "Key points during the Jumping",
    items: [
      {
        name: "Port Hercule",
        description: "Quays hosting the temporary show-jumping arena.",
      },
      {
        name: "Carré d'Or & Casino",
        description: "Hôtel de Paris, Hermitage and Place du Casino.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description: "Terminals 1 and 2, about thirty minutes from Monaco.",
      },
      {
        name: "Fairmont & Monte-Carlo Bay",
        description: "Seafront hotels and hospitality.",
      },
      {
        name: "Beausoleil & surroundings",
        description: "Residences and access from the heights above Monaco.",
      },
    ],
  },
  generic: {
    heading: "The Jumping International de Monaco from a chauffeur's seat",
    subheading:
      "In late June, the show-jumping arena rises on the quays of Port Hercule and central Monaco tightens.",
    paragraphs: [
      "Every late June, the Jumping International de Monaco builds a temporary arena on the quays of Port Hercule for a leg of the Longines Global Champions Tour. Port traffic is reduced and parking, already scarce in Monaco, becomes impossible: a chauffeur who knows the authorised drop-offs gets you near the entrance without circling.",
      "Most arrivals come through Nice Côte d'Azur airport, about thirty minutes away. With flight tracking and a name-board welcome, we absorb delays to take you straight to your Carré d'Or or seafront hotel.",
      "Classes run across the weekend, day and evening, between paddock, hospitality and dinners. Hourly hire keeps the same chauffeur all day and secures the late-night returns.",
    ],
    bullets: [
      "Drop-off nearest Port Hercule",
      "Flight tracking in late June",
      "Late-night returns after the classes",
    ],
  },
  process: {
    heading: "Booking a chauffeur for the Jumping de Monaco",
    steps: [
      "Tell us your weekend dates, your hotel and the classes you plan to attend.",
      "We plan the routes around the Port Hercule closures and Monaco's crowds.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Direct transfer or hourly hire to cover classes, hospitality and evenings.",
    ],
  },
  faq: [
    {
      q: "How do I book a private chauffeur for the Jumping International de Monaco?",
      a: "Book early: in late June, Monaco combines the Jumping with heavy summer demand. Share your dates, your hotel and the classes you want to attend so we can secure a dedicated chauffeur across the weekend.",
    },
    {
      q: "Can you drop me as close as possible to Port Hercule?",
      a: "Yes. During the event, the quays host the show-jumping arena and traffic is reduced. The chauffeur drops you at the nearest authorised point to the entrance, then collects you on the way out.",
    },
    {
      q: "Do you handle arrivals from Nice airport?",
      a: "Yes, with flight tracking and a name-board welcome. Nice Côte d'Azur airport is about thirty minutes away; we adjust the pick-up time if your flight is delayed and take you straight into Monaco.",
    },
    {
      q: "Do you offer full-day hire?",
      a: "Yes. A dedicated chauffeur stays at your disposal by the hour or day to chain classes, hospitality and meetings without worrying about Monaco parking.",
    },
    {
      q: "Do you cover late returns after the evenings?",
      a: "Yes. Carré d'Or dinners and hospitality end late: the chauffeur waits and guarantees your late-night return to your hotel or residence.",
    },
    {
      q: "Why choose KS Rent Services for the Jumping?",
      a: "Our private chauffeur service on the French Riviera is rated 5.0/5 from 38 Google reviews: a uniformed, punctual and discreet chauffeur used to Monaco's access and parking constraints.",
    },
  ],
  relatedLinks: [
    { label: "Private chauffeur in Monaco", href: "/en/chauffeur/monaco" },
    { label: "Car service in Nice", href: "/en/chauffeur/nice" },
    {
      label: "Meet and greet at Nice Airport",
      href: "/en/chauffeur/airport",
    },
    {
      label: "Car rental in Monaco",
      href: "/en/location/car-rental-monaco",
    },
  ],
  cta: {
    title: "Your chauffeur for the Jumping de Monaco",
    text: "Airport welcome, Port Hercule access and evenings: book a dedicated chauffeur for the Jumping weekend.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Jumping International Monaco Chauffeur — VIP car service",
    description:
      "Private chauffeur during the Jumping International de Monaco: Port Hercule access, Nice airport meet-and-greet, hourly hire and late-night returns. Book now.",
    canonical: "/en/chauffeur/jumping-international-monaco",
  },
};

export default jumpingInternationalMonacoEn;
